import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { InteractivePlayer, type PlayerPlaybackState } from '../components/InteractivePlayer'
import { useDirty } from '../contexts/DirtyContext'
import { api, ApiError } from '../lib/api'
import {
  createInitialConfig,
  deriveProjectNodes,
  findPlaybackNodeAtTime,
  normalizeProjectConfig,
  OPENING_LYRIC_MAX_LENGTH,
  parseYouTubeVideoId,
  validateProjectConfig,
  type ProjectConfig,
  type ProjectDetail,
  type ProjectEdge,
  type ProjectNode,
  type AttributionMode,
  type Visibility,
} from '../lib/project'

type SearchResult = { videoId: string; title: string; channelTitle: string; thumbnailUrl?: string }
type SearchResponse = { results: SearchResult[] }
type VerifiedVideo = { videoId: string; title: string; channelTitle: string }
type SearchState = 'idle' | 'loading' | 'ready' | 'empty' | 'error'
type VerificationState = 'idle' | 'verifying' | 'error'
type PendingAlignment = { node: ProjectNode; previousNode: ProjectNode }
type PreviewableNode = Pick<ProjectNode, 'id' | 'label' | 'start'> & { end?: number | null }

const emptyConfig = (): ProjectConfig => createInitialConfig('')
const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainder = (seconds % 60).toFixed(1).padStart(4, '0')
  return `${String(minutes).padStart(2, '0')}:${remainder}`
}

type TimeDraft = { minutes: string; seconds: string; tenths: string }
const emptyTimeDraft: TimeDraft = { minutes: '', seconds: '', tenths: '' }

function timeDraftFromSeconds(value: number): TimeDraft {
  const tenths = Math.round(value * 10)
  return {
    minutes: String(Math.floor(tenths / 600)),
    seconds: String(Math.floor(tenths / 10) % 60).padStart(2, '0'),
    tenths: String(tenths % 10),
  }
}

function isBlockedSearchInput(value: string) {
  return Boolean(parseYouTubeVideoId(value)) || /(?:https?:\/\/)?(?:www\.)?(?:youtu\.be|youtube(?:-nocookie)?\.com)\b/i.test(value)
}

export function EditorPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { dirty, setDirty } = useDirty()
  const [description, setDescription] = useState('')
  const [config, setConfig] = useState<ProjectConfig>(emptyConfig)
  const [revision, setRevision] = useState<number | null>(null)
  const [visibility, setVisibility] = useState<Visibility>('private')
  const [attributionMode, setAttributionMode] = useState<AttributionMode>('named')
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [loading, setLoading] = useState(Boolean(projectId))
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [videoSuspended, setVideoSuspended] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchState, setSearchState] = useState<SearchState>('idle')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchOpen, setSearchOpen] = useState(!projectId)
  const [searchInputError, setSearchInputError] = useState('')
  const [verificationState, setVerificationState] = useState<VerificationState>('idle')
  const [verificationError, setVerificationError] = useState('')
  const [verifyingVideoId, setVerifyingVideoId] = useState<string | null>(null)
  const [playerTime, setPlayerTime] = useState(0)
  const [confirmedPausedPosition, setConfirmedPausedPosition] = useState<number | null>(null)
  const [editorPreviewBusy, setEditorPreviewBusy] = useState(false)
  const [editorPreviewReady, setEditorPreviewReady] = useState(false)
  const [connectionPreviewSeconds, setConnectionPreviewSeconds] = useState('5.0')
  const [connectionPreviewTouched, setConnectionPreviewTouched] = useState(false)
  const [connectionPreview, setConnectionPreview] = useState<{ sourceLabel: string; candidateLabel: string; phase: 1 | 2 } | null>(null)
  const [playbackState, setPlaybackState] = useState<PlayerPlaybackState>({
    currentNodeId: null,
    playing: false,
    switching: false,
    status: 'unloaded',
    playbackIntent: false,
  })
  const [startTimeDraft, setStartTimeDraft] = useState<TimeDraft>(emptyTimeDraft)
  const [endTimeDraft, setEndTimeDraft] = useState<TimeDraft>(emptyTimeDraft)
  const [durationError, setDurationError] = useState('')
  const [pendingVideo, setPendingVideo] = useState<VerifiedVideo | null>(null)
  const [pendingAlignment, setPendingAlignment] = useState<PendingAlignment | null>(null)
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false)
  const [saveAttempted, setSaveAttempted] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const selectedSourceHeadingRef = useRef<HTMLHeadingElement>(null)
  const selectButtonRefs = useRef(new Map<string, HTMLButtonElement>())
  const nodeLabelInputRef = useRef<HTMLInputElement>(null)
  const startInputRef = useRef<HTMLInputElement>(null)
  const endInputRef = useRef<HTMLInputElement>(null)
  const cancelDialogRef = useRef<HTMLButtonElement>(null)
  const editorSelectionRequestRef = useRef<((nodeId: string) => void) | null>(null)
  const editorPreviewRequestRef = useRef<((source: PreviewableNode, candidate: PreviewableNode, seconds: number, trigger: HTMLElement) => boolean) | null>(null)
  const pendingSaveConfigRef = useRef<ProjectConfig | null>(null)

  const hasSource = Boolean(config.video.videoId && config.video.title.trim())
  const orderedNodes = useMemo(() => deriveProjectNodes(config.nodes, config.video.duration), [config.nodes, config.video.duration])
  const selectedNode = config.nodes.find((node) => node.id === selectedNodeId) ?? null
  const selectedNodeStart = selectedNode?.start
  const selectedNodeEnd = selectedNode?.end
  const validationErrors = useMemo(() => {
    if (!hasSource) return ['請先選擇 YouTube 影片']
    return [...new Set(validateProjectConfig(config))]
  }, [config, hasSource])
  const durationReady = Number.isFinite(config.video.duration) && (config.video.duration ?? 0) > 0
  const sourceReady = hasSource && durationReady
  const connectionPreviewValid = Number.isFinite(Number(connectionPreviewSeconds))
    && Number(connectionPreviewSeconds) >= 0.5 && Number(connectionPreviewSeconds) <= 10
    && Math.abs(Number(connectionPreviewSeconds) * 10 - Math.round(Number(connectionPreviewSeconds) * 10)) < 1e-8
  const nodeValidationErrors = useMemo(() => validationErrors.filter((item) => !item.includes('連接引用')), [validationErrors])
  const nodesReady = sourceReady && config.nodes.length > 0 && nodeValidationErrors.length === 0
  const timeDraftError = useMemo(() => {
    if (!selectedNode) return ''
    const parseDraft = (draft: TimeDraft, kind: '開始' | '結束', allowEmpty = false) => {
      if (allowEmpty && !draft.minutes.trim() && !draft.seconds.trim() && !draft.tenths.trim()) return { empty: true }
      if (!draft.minutes.trim() || !draft.seconds.trim() || !draft.tenths.trim()) return { error: `請完整輸入${kind}時間。` }
      const minutes = Number(draft.minutes); const seconds = Number(draft.seconds); const tenths = Number(draft.tenths)
      if (!Number.isInteger(minutes) || minutes < 0) return { error: `請完整輸入${kind}時間。` }
      if (!Number.isInteger(seconds) || seconds < 0 || seconds > 59) return { error: '秒必須介於 0 到 59。' }
      if (!Number.isInteger(tenths) || tenths < 0 || tenths > 9) return { error: '0.1 秒必須介於 0 到 9。' }
      return { value: minutes * 60 + seconds + tenths / 10 }
    }
    const startResult = parseDraft(startTimeDraft, '開始')
    const endResult = parseDraft(endTimeDraft, '結束', true)
    if (startResult.error) return startResult.error
    if (endResult.error) return endResult.error
    if (endResult.empty) return ''
    const start = startResult.value!
    const end = endResult.value!
    if (!durationReady || end > (config.video.duration ?? 0)) return '結束時間不得晚於影片結束時間。'
    if (start >= end) return '開始時間必須早於結束時間。'
    return ''
  }, [config.video.duration, durationReady, endTimeDraft, selectedNode, startTimeDraft])
  const effectiveValidationErrors = useMemo(
    () => [...new Set(timeDraftError ? [...validationErrors, timeDraftError] : validationErrors)],
    [timeDraftError, validationErrors],
  )
  const saveReason = useMemo(() => {
    if (!hasSource) return '請先選擇 YouTube 影片。'
    if (!durationReady) return '請先取得影片總長度。'
    if (!config.nodes.length) return '請至少新增一個段落。'
    return effectiveValidationErrors[0] ?? ''
  }, [config.nodes.length, durationReady, effectiveValidationErrors, hasSource])

  useEffect(() => {
    setStartTimeDraft(selectedNodeStart === undefined ? emptyTimeDraft : timeDraftFromSeconds(selectedNodeStart))
    setEndTimeDraft(selectedNodeEnd === undefined ? emptyTimeDraft : timeDraftFromSeconds(selectedNodeEnd))
  }, [selectedNodeEnd, selectedNodeStart])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  useEffect(() => {
    if (projectId) return
    searchInputRef.current?.focus()
  }, [projectId])

  useEffect(() => {
    if (!projectId) {
      setDirty(false)
      return
    }
    let active = true
    setLoading(true)
    void api<ProjectDetail>(`/api/projects/${projectId}`)
      .then((project) => {
        if (!active) return
        if (project.videoSuspended) { setVideoSuspended(true); setDirty(false); return }
        const normalized = normalizeProjectConfig(project.config)
        setDescription(project.description)
        setConfig(normalized)
        setRevision(project.revision)
        setVisibility(project.visibility)
        setAttributionMode(project.attributionMode ?? 'named')
        setSelectedNodeId(normalized.playbackSettings.initialNodeId ?? normalized.nodes[0]?.id ?? null)
        setSearchOpen(!normalized.video.title)
        setDirty(false)
      })
      .catch((caught: ApiError) => active && setError(caught.message))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [projectId, setDirty])

  const change = (updater: (previous: ProjectConfig) => ProjectConfig) => {
    setConfig(updater)
    setDirty(true)
    setMessage('')
  }

  const search = async () => {
    const query = searchQuery.trim()
    if (!query) {
      setSearchInputError('請輸入歌曲、歌手或頻道名稱。')
      return
    }
    if (isBlockedSearchInput(query)) {
      setSearchInputError('請輸入歌曲、歌手或頻道名稱來搜尋。')
      return
    }
    setSearchInputError('')
    setSearchState('loading')
    try {
      const response = await api<SearchResponse>(`/api/youtube/search?q=${encodeURIComponent(query)}`)
      setSearchResults(response.results.slice(0, 10))
      setSearchState(response.results.length ? 'ready' : 'empty')
    } catch {
      setSearchState('error')
    }
  }

  const applyVerifiedVideo = (verified: VerifiedVideo) => {
    const replacingSource = hasSource && config.video.videoId !== verified.videoId
    const firstNode: ProjectNode = { id: crypto.randomUUID(), label: '段落 1', start: 0 }
    change((previous) => ({
      ...previous,
      schemaVersion: 3,
      video: {
        ...previous.video,
        provider: 'youtube',
        videoId: verified.videoId,
        duration: null,
        title: verified.title,
        channelTitle: verified.channelTitle,
      },
      nodes: replacingSource || previous.nodes.length === 0 ? [firstNode] : previous.nodes,
      edges: replacingSource ? [] : previous.edges,
      playbackSettings: {
        ...previous.playbackSettings,
        initialNodeId: replacingSource || previous.nodes.length === 0
          ? firstNode.id
          : previous.playbackSettings.initialNodeId,
      },
    }))
    if (replacingSource || !selectedNodeId) setSelectedNodeId(firstNode.id)
    setSearchOpen(false)
    setSearchResults([])
    setSearchState('idle')
    setVerificationState('idle')
    setVerifyingVideoId(null)
    setPendingVideo(null)
    window.setTimeout(() => selectedSourceHeadingRef.current?.focus(), 0)
  }

  const selectVideo = async (result: SearchResult) => {
    setError('')
    setVerificationError('')
    setDurationError('')
    setVerificationState('verifying')
    setVerifyingVideoId(result.videoId)
    try {
      const verified = await api<VerifiedVideo>('/api/youtube/verify', { method: 'POST', body: JSON.stringify({ videoId: result.videoId }) })
      if (hasSource && config.video.videoId !== verified.videoId) {
        setPendingVideo(verified)
        window.setTimeout(() => cancelDialogRef.current?.focus(), 0)
      } else {
        applyVerifiedVideo(verified)
      }
    } catch (caught) {
      const apiError = caught instanceof ApiError ? caught : null
      const messageByCode: Record<string, string> = {
        VIDEO_UNAVAILABLE: '這支影片不是公開影片，請選擇其他影片。',
        VIDEO_POLICY_BLOCKED: '影片擁有者不允許嵌入播放，請選擇其他影片。',
        VIDEO_ID_INVALID: '這支影片目前無法用來建立作品，請選擇其他影片。',
      }
      setVerificationError(messageByCode[apiError?.code ?? ''] ?? '目前無法驗證這支 YouTube 影片，請稍後再試。')
      setVerificationState('error')
      window.setTimeout(() => selectButtonRefs.current.get(result.videoId)?.focus(), 0)
    }
  }

  const cancelPendingVideo = () => {
    setPendingVideo(null)
    setVerificationState('idle')
    setVerifyingVideoId(null)
  }

  const handleSearchInput = (value: string) => {
    if (isBlockedSearchInput(value)) {
      setSearchInputError('請輸入歌曲、歌手或頻道名稱來搜尋。')
      return
    }
    setSearchQuery(value)
    setSearchInputError('')
  }

  const handleDuration = useCallback((duration: number) => {
    if (config.video.duration === duration) {
      setDurationError('')
      return
    }
    setConfig((previous) => {
      const ordered = [...previous.nodes].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
      const isFreshFirstSegment = previous.video.duration === null && ordered.length === 1 && ordered[0]?.start === 0
      return {
        ...previous,
        video: { ...previous.video, duration },
        nodes: ordered.map((node, index) => isFreshFirstSegment && index === 0 ? { ...node, end: duration } : node),
      }
    })
    setDurationError('')
    setDirty(true)
  }, [config.video.duration, setDirty])

  const handlePlaybackError = useCallback(() => {
    setDurationError('無法讀取影片長度，暫時不能設定段落。請重試載入影片或重新搜尋影片。')
  }, [])

  const addNode = () => {
    const duration = config.video.duration
    const start = confirmedPausedPosition
    if (!duration || start === null || start < 0 || start >= duration) {
      setError('請先暫停影片並選擇新增段落位置。')
      return
    }
    const node: ProjectNode = {
      id: crypto.randomUUID(),
      label: `段落 ${config.nodes.length + 1}`,
      start,
      end: duration,
    }
    const previousNode = [...config.nodes]
      .filter((item) => item.start < start)
      .sort((a, b) => b.start - a.start)[0]
    if (previousNode && (!Number.isFinite(previousNode.end) || (previousNode.end as number) > start)) {
      setPendingAlignment({ node, previousNode })
      window.setTimeout(() => cancelDialogRef.current?.focus(), 0)
      return
    }
    addPendingNode(node, false)
  }

  const addPendingNode = (node: ProjectNode, alignPrevious: boolean) => {
    change((previous) => ({
      ...previous,
      nodes: [...previous.nodes.map((item) => (
        alignPrevious && pendingAlignment && item.id === pendingAlignment.previousNode.id
          ? { ...item, end: node.start }
          : item
      )), node].sort((a, b) => a.start - b.start),
      playbackSettings: { ...previous.playbackSettings, initialNodeId: previous.playbackSettings.initialNodeId ?? node.id },
    }))
    setSelectedNodeId(node.id)
    setPendingAlignment(null)
    window.setTimeout(() => nodeLabelInputRef.current?.focus(), 0)
  }

  const updateNode = (patch: Partial<ProjectNode>) => {
    if (!selectedNode) return
    change((previous) => ({ ...previous, nodes: previous.nodes.map((node) => node.id === selectedNode.id ? { ...node, ...patch } : node).sort((a, b) => a.start - b.start) }))
  }

  const handleEditorSelectionRequest = useCallback((request: ((nodeId: string) => void) | null) => {
    editorSelectionRequestRef.current = request
  }, [])

  const handleEditorPreviewRequest = useCallback((request: ((source: PreviewableNode, candidate: PreviewableNode, seconds: number, trigger: HTMLElement) => boolean) | null) => {
    editorPreviewRequestRef.current = request
  }, [])

  const startConnectionPreview = (candidate: PreviewableNode, trigger: HTMLElement) => {
    if (!selectedNode || !connectionPreviewValid || editorPreviewBusy || !editorPreviewReady) return
    const accepted = editorPreviewRequestRef.current?.(selectedNode, candidate, Number(connectionPreviewSeconds), trigger) ?? false
    if (!accepted) {
      setConnectionPreview(null)
      return
    }
    setEditorPreviewBusy(true)
    setConnectionPreview({ sourceLabel: selectedNode.label, candidateLabel: candidate.label, phase: 1 })
  }

  const selectNode = (nodeId: string, requestPlayback = false) => {
    setSelectedNodeId(nodeId)
    const node = config.nodes.find((item) => item.id === nodeId)
    setStartTimeDraft(node ? timeDraftFromSeconds(node.start) : emptyTimeDraft)
    setEndTimeDraft(node?.end === undefined ? emptyTimeDraft : timeDraftFromSeconds(node.end))
    if (requestPlayback) editorSelectionRequestRef.current?.(nodeId)
  }

  const finalizeTimeDraft = () => {
    if (!selectedNode || timeDraftError) return null
    const start = Number(startTimeDraft.minutes) * 60 + Number(startTimeDraft.seconds) + Number(startTimeDraft.tenths) / 10
    const hasEnd = Boolean(endTimeDraft.minutes.trim() || endTimeDraft.seconds.trim() || endTimeDraft.tenths.trim())
    const end = hasEnd
      ? Number(endTimeDraft.minutes) * 60 + Number(endTimeDraft.seconds) + Number(endTimeDraft.tenths) / 10
      : undefined
    if (selectedNode.start === start && selectedNode.end === end) return config
    const finalized = {
      ...config,
      nodes: config.nodes
        .map((node) => node.id === selectedNode.id ? { ...node, start, end } : node)
        .sort((a, b) => a.start - b.start),
    }
    setConfig(finalized)
    return finalized
  }

  const commitTimeDraft = () => Boolean(finalizeTimeDraft())

  const resetTimeDraft = () => {
    setStartTimeDraft(selectedNode ? timeDraftFromSeconds(selectedNode.start) : emptyTimeDraft)
    setEndTimeDraft(selectedNode?.end === undefined ? emptyTimeDraft : timeDraftFromSeconds(selectedNode.end))
  }

  const removeNode = () => {
    if (!selectedNode || !window.confirm(`刪除段落「${selectedNode.label}」？刪除範圍會併入前一個段落；指向此段的可選下一段也會移除。`)) return
    const chronological = [...config.nodes].sort((a, b) => a.start - b.start)
    const removedIndex = chronological.findIndex((node) => node.id === selectedNode.id)
    const previousNode = removedIndex > 0 ? chronological[removedIndex - 1] : null
    const remaining = chronological
      .filter((node) => node.id !== selectedNode.id)
      .map((node) => node.id === previousNode?.id ? { ...node, end: selectedNode.end } : node)
    change((previous) => ({
      ...previous,
      nodes: remaining,
      edges: previous.edges.filter((edge) => edge.from !== selectedNode.id && edge.to !== selectedNode.id),
      playbackSettings: { ...previous.playbackSettings, initialNodeId: previous.playbackSettings.initialNodeId === selectedNode.id ? remaining[0]?.id ?? null : previous.playbackSettings.initialNodeId },
    }))
    setSelectedNodeId(previousNode?.id ?? remaining[0]?.id ?? null)
  }

  const toggleEdge = (targetId: string, enabled: boolean) => {
    if (!selectedNode) return
    change((previous) => {
      const existing = previous.edges.filter((edge) => !(edge.from === selectedNode.id && edge.to === targetId))
      return { ...previous, edges: enabled ? [...existing, { from: selectedNode.id, to: targetId, label: '可選下一段', order: existing.filter((edge) => edge.from === selectedNode.id).length + 1 } satisfies ProjectEdge] : existing }
    })
  }

  const validateBeforeSave = () => {
    setError('')
    setMessage('')
    setSaveAttempted(true)
    const finalized = finalizeTimeDraft()
    const finalizedErrors = [
      ...new Set([
        ...(!hasSource ? ['請先選擇 YouTube 影片'] : finalized ? validateProjectConfig(finalized) : validationErrors),
        ...(timeDraftError ? [timeDraftError] : []),
      ]),
    ]
    if (!finalized || finalizedErrors.length) {
      setError(finalizedErrors[0] ?? '請檢查段落時間。')
      const missingEnd = (finalized ?? config).nodes.find((node) => !Number.isFinite(node.end))
      if (missingEnd) {
        setSelectedNodeId(missingEnd.id)
        window.setTimeout(() => endInputRef.current?.focus(), 0)
      } else {
        ;(hasSource ? startInputRef.current : searchInputRef.current)?.focus()
      }
      return null
    }
    return finalized
  }

  const requestSave = () => {
    const finalized = validateBeforeSave()
    if (!finalized) return
    if (visibility === 'private') {
      pendingSaveConfigRef.current = finalized
      setSaveConfirmationOpen(true)
      window.setTimeout(() => cancelDialogRef.current?.focus(), 0)
    } else {
      void save(false, finalized)
    }
  }

  const save = async (publish: boolean, finalizedConfig?: ProjectConfig) => {
    const finalized = finalizedConfig ?? validateBeforeSave()
    if (!finalized) return
    pendingSaveConfigRef.current = null
    setSaveConfirmationOpen(false)
    setSaving(true)
    try {
      const payload = {
        description: description.trim(),
        videoId: finalized.video.videoId,
        config: normalizeProjectConfig(finalized),
        ...(revision ? { revision } : {}),
        publish,
        attributionMode: attributionMode ?? 'named',
      }
      const saved = await api<ProjectDetail>(projectId ? `/api/projects/${projectId}` : '/api/projects', { method: projectId ? 'PUT' : 'POST', body: JSON.stringify(payload) })
      setRevision(saved.revision)
      setConfig(normalizeProjectConfig(saved.config))
      setVisibility(saved.visibility)
      setAttributionMode(saved.attributionMode)
      setDirty(false)
      setSaveAttempted(false)
      setMessage('已儲存')
      if (!projectId) navigate(`/projects/${saved.id}/edit`, { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : '暫時無法儲存，草稿仍保留在此頁')
    } finally { setSaving(false) }
  }

  const saveDisabled = saving || !dirty
  const saveStatus = saving ? '儲存中…' : dirty ? '尚未儲存' : revision ? '已儲存' : '尚未建立'
  const saveHint = saving ? '' : dirty && effectiveValidationErrors.length > 0 ? saveReason : !dirty ? '修改後可手動儲存。' : ''
  const playbackNode = findPlaybackNodeAtTime(config.nodes, config.video.duration, playerTime)
  const playbackAnnouncement = playbackNode
    ? playbackState.switching ? `${playbackNode.label}，切換中…` : playbackState.playing ? `${playbackNode.label}，播放中` : `${playbackNode.label}，目前段落，已暫停`
    : ''
  const renderSaveArea = (location: 'segments' | 'connections') => (
    <div className={`save-area save-area--${location}`} aria-label={location === 'segments' ? '段落設定儲存操作' : '可選連接儲存操作'}>
      <div><span className={`save-status ${dirty ? 'save-status--dirty' : ''}`}>{saveStatus}</span>{saveHint && <small className="save-reason">{saveHint}</small>}</div>
      <button className="button button--primary" type="button" disabled={saveDisabled} onClick={requestSave}>{saving ? '儲存中…' : '儲存'}</button>
    </div>
  )

  const renderNodeButton = (node: typeof orderedNodes[number], index: number, source = false) => {
    const isSelected = node.id === selectedNodeId
    const isCurrent = node.id === playbackNode?.id
    const stateText = isCurrent ? playbackState.switching ? '切換中…' : playbackState.playing ? '播放中' : '目前段落' : ''
    const isPlaying = isCurrent && playbackState.playing
    const playbackClass = !source && isPlaying ? ' editor-node--playing' : ''
    const nodeStateText = source ? '' : stateText
    return <button type="button" disabled={editorPreviewBusy} className={`editor-node${isSelected ? ' editor-node--selected' : ''}${playbackClass}`} aria-pressed={isSelected} aria-current={!source && isCurrent || undefined} aria-label={`${node.label}${nodeStateText ? `，${nodeStateText}${!playbackState.playing && !playbackState.switching ? '，已暫停' : ''}` : ''}`} onClick={() => selectNode(node.id, !source)} key={node.id}>
      <span>{String(index + 1).padStart(2, '0')}</span><strong>{node.label}</strong>
      {source ? <small>{config.edges.filter((edge) => edge.from === node.id).length ? `已設定 ${config.edges.filter((edge) => edge.from === node.id).length} 個可選連接` : '尚未設定可選連接'}</small> : <small>{formatTime(node.start)}–{node.end === null ? '…' : formatTime(node.end)}</small>}
      {source ? node.openingLyric ? <em className="editor-node__lyric">開頭歌詞：{node.openingLyric}</em> : <em>尚未填寫開頭歌詞</em> : node.openingLyric ? <em className="editor-node__lyric">開頭歌詞：{node.openingLyric}</em> : node.end === config.video.duration && <em>影片結束</em>}
    </button>
  }

  if (loading) return <div className="page-status">正在載入作品…</div>
  if (videoSuspended) return <div className="page narrow-page"><div className="empty-state"><h1>影片目前無法播放</h1><p>這支影片目前無法在 iLoveSing 使用。</p><Link className="button button--primary" to="/projects">回到我的作品</Link></div></div>

  return (
    <div className="editor-page">
      <header className="editor-header"><div><Link className="text-link" to="/projects">← 我的作品</Link><h1>{projectId ? '編輯作品' : '建立作品'}</h1></div><span className={`save-status ${dirty ? 'save-status--dirty' : ''}`} aria-live="polite">{saveStatus}</span></header>
      {message && <div className="alert alert--success editor-alert" role="status">{message}</div>}
      {error && <div className="alert alert--danger editor-alert" role="alert">{error}</div>}
      <div className="editor-layout">
        <section className="panel editor-block" aria-labelledby="source-heading"><div className="panel-heading"><div className="editor-block__title"><span className={`editor-block__number ${hasSource ? 'editor-block__number--ready' : ''}`}>1</span><div><h2 id="source-heading">選擇 YouTube 影片</h2><span className="editor-block__status">{hasSource ? '已選擇' : verificationState === 'error' ? '需要處理' : '尚未選擇'}</span></div></div></div>
          {searchOpen && <section className="youtube-source-search" aria-label="搜尋 YouTube 影片">
            <form className="youtube-source-search__form" onSubmit={(event) => { event.preventDefault(); void search() }}>
              <div className="field"><label htmlFor="youtube-search">搜尋 YouTube 影片</label><input ref={searchInputRef} id="youtube-search" aria-describedby="youtube-search-help" value={searchQuery} onChange={(event) => handleSearchInput(event.target.value)} placeholder="輸入歌曲、歌手或頻道名稱" disabled={searchState === 'loading'} /><small id="youtube-search-help">請用歌曲、歌手或頻道名稱搜尋後選擇影片。</small></div>
              <button className="button button--primary" type="submit" disabled={!searchQuery.trim() || searchState === 'loading'}>{searchState === 'loading' ? '搜尋中…' : '搜尋'}</button>
            </form>
            {searchInputError && <div className="alert alert--danger" role="alert">{searchInputError}</div>}
            {searchState === 'loading' && <p className="page-status youtube-source-search__status" role="status">正在搜尋 YouTube 影片…</p>}
            {searchState === 'empty' && <p className="youtube-source-search__status" role="status">找不到符合的影片，請換個關鍵字再試。</p>}
            {searchState === 'error' && <div className="alert alert--danger" role="alert">目前無法搜尋 YouTube 影片，請稍後再試。 <button className="text-button" type="button" onClick={() => void search()}>重新搜尋</button></div>}
            {verificationState === 'error' && <div className="alert alert--danger" role="alert">{verificationError} {verifyingVideoId && <button className="text-button" type="button" onClick={() => { const result = searchResults.find((item) => item.videoId === verifyingVideoId); if (result) void selectVideo(result) }}>重試驗證</button>}</div>}
            {searchResults.length > 0 && <ul className="youtube-search-results" aria-label="YouTube 搜尋結果">{searchResults.map((result) => {
              const verifying = verificationState === 'verifying' && verifyingVideoId === result.videoId
              return <li className={`youtube-search-result ${verifying ? 'youtube-search-result--selected' : ''}`} key={result.videoId}>
                {result.thumbnailUrl && <img src={result.thumbnailUrl} alt="" />}
                <div><strong title={result.title}>{result.title}</strong><span>{result.channelTitle}</span>{verifying && <small role="status">正在驗證這支影片…</small>}</div>
                <button ref={(element) => { if (element) selectButtonRefs.current.set(result.videoId, element); else selectButtonRefs.current.delete(result.videoId) }} className="button" type="button" disabled={verificationState === 'verifying'} onClick={() => void selectVideo(result)} aria-label={`選擇這支影片：${result.title}`}>選擇這支影片</button>
              </li>
            })}</ul>}
          </section>}
          {hasSource && <div className="selected-youtube-source"><div>{config.video.videoId && <img src={`https://i.ytimg.com/vi/${config.video.videoId}/mqdefault.jpg`} alt="" />}<div><span>已選擇的 YouTube 影片</span><h3 tabIndex={-1} ref={selectedSourceHeadingRef}>已選擇：{config.video.title}</h3><p>{config.video.channelTitle}</p></div></div><button className="button" type="button" onClick={() => { setSearchOpen(true); setVerificationError(''); window.setTimeout(() => searchInputRef.current?.focus(), 0) }}>重新搜尋影片</button></div>}
          {hasSource && !durationReady && <p className="youtube-source-search__status" role="status">正在讀取影片長度，完成後可在第 2 區設定段落。</p>}
        </section>
        <section className={`panel editor-block editor-block--segments ${!sourceReady ? 'editor-block--disabled' : ''}`} aria-labelledby="segments-heading" aria-disabled={!sourceReady}><div className="panel-heading"><div className="editor-block__title"><span className={`editor-block__number ${nodesReady ? 'editor-block__number--ready' : ''}`}>2</span><div><h2 id="segments-heading">設定段落／節點</h2><span className="editor-block__status">{nodesReady ? '段落已就緒' : sourceReady ? '待完成段落' : '等待影片'}</span></div></div></div>
          {!hasSource ? (
            <>
              <p className="editor-block__notice">請先在第 1 區選擇可用的 YouTube 影片。</p>
              <div className="editor-settings"><div className="editor-settings__heading"><h3>節點設定</h3><button className="button button--primary" type="button" onClick={addNode} disabled>＋ 新增段落</button></div><p className="muted">請先選擇影片並取得影片長度後，再新增段落。</p></div>
            </>
          ) : (
            <>
              <div className="form-grid"><div className="field"><span>歌曲名稱</span><output className="readonly-value" title={config.video.title}>{config.video.title}</output><small>由 YouTube 提供</small></div><div className="field"><span>YouTube 頻道</span><output className="readonly-value">{config.video.channelTitle}</output></div><label className="field field--wide"><span>關於這個作品（選填）</span><input value={description} maxLength={1000} onChange={(event) => { setDescription(event.target.value); setDirty(true) }} placeholder="例如：敬拜團排練用；副歌請重複兩次。" /><small>說明這個編排的使用情境，不會變更歌曲名稱。</small></label></div>
              <InteractivePlayer key={config.video.videoId} config={config} showNextChoices={false} onDurationReady={handleDuration} onPlaybackError={handlePlaybackError} onCurrentTimeChange={setPlayerTime} onPlaybackStateChange={setPlaybackState} onEditorSelectionRequestReady={handleEditorSelectionRequest} onConfirmedPausedPosition={setConfirmedPausedPosition} onEditorBusyChange={setEditorPreviewBusy} onEditorPreviewRequestReady={handleEditorPreviewRequest} onEditorPreviewReadyChange={setEditorPreviewReady} onEditorPreviewStateChange={setConnectionPreview} />
              {!durationReady ? (
                <>
                  <p className={`editor-block__notice ${durationError ? 'editor-block__notice--error' : ''}`} role={durationError ? 'alert' : 'status'}>{durationError || '正在讀取影片長度…'}</p>
                  <div className="editor-settings"><div className="editor-settings__heading"><h3>節點設定</h3><button className="button button--primary" type="button" onClick={addNode} disabled>＋ 新增段落</button></div><p className="muted">請先取得影片長度後，再新增段落。</p></div>
                </>
              ) : (
                <>
                  <p className="muted">影片總長度：{formatTime(config.video.duration!)}</p>
                  <div className="editor-section-layout">
                    <div>{orderedNodes.length ? <div className="node-list node-list--segments" aria-label="段落列表">{orderedNodes.map((node, index) => renderNodeButton(node, index))}</div> : <div className="empty-state">在播放器目前位置新增第一個段落。</div>}</div>
                    <div className="editor-settings">
                      <div className="editor-settings__heading"><h3>節點設定</h3><button className="button button--primary" type="button" onClick={addNode} disabled={!sourceReady || editorPreviewBusy || confirmedPausedPosition === null} aria-describedby={editorPreviewBusy ? 'add-node-preview-hint' : 'add-node-position-hint'}>＋ 新增段落</button></div>
                      {editorPreviewBusy ? <p id="add-node-preview-hint" className="muted">請等待定位或試聽完成後再新增段落。</p> : <p id="add-node-position-hint" className="muted">請先暫停影片並選擇新增段落位置。</p>}
                      {selectedNode ? (
                        <>
                          <label className="field"><span>段落名稱</span><input ref={nodeLabelInputRef} value={selectedNode.label} maxLength={60} onChange={(event) => updateNode({ label: event.target.value })} /></label>
                          <label className="field opening-lyric-field"><span>開頭歌詞（選填）</span><textarea value={selectedNode.openingLyric ?? ''} maxLength={OPENING_LYRIC_MAX_LENGTH} onChange={(event) => updateNode({ openingLyric: event.target.value })} placeholder="例如：我站在祢面前" /><small>這句文字屬於「{selectedNode.label}」；播放到這個段落時，會顯示在該段落列。</small></label>
                          <fieldset className="time-input-group" aria-describedby={timeDraftError ? 'start-time-error' : undefined} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) commitTimeDraft() }}>
                            <legend>開始時間</legend>
                            <div className="time-inputs">
                              <label className="field"><span>分</span><input ref={startInputRef} type="number" min="0" step="1" inputMode="numeric" value={startTimeDraft.minutes} aria-invalid={Boolean(timeDraftError)} aria-describedby={timeDraftError ? 'start-time-error' : undefined} onChange={(event) => { setStartTimeDraft((value) => ({ ...value, minutes: event.target.value })); setDirty(true) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTimeDraft() } if (event.key === 'Escape') resetTimeDraft() }} /></label>
                              <span className="time-separator" aria-hidden="true">：</span>
                              <label className="field"><span>秒</span><input type="number" min="0" max="59" step="1" inputMode="numeric" value={startTimeDraft.seconds} aria-invalid={timeDraftError === '秒必須介於 0 到 59。'} aria-describedby={timeDraftError ? 'start-time-error' : undefined} onChange={(event) => { setStartTimeDraft((value) => ({ ...value, seconds: event.target.value })); setDirty(true) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTimeDraft() } if (event.key === 'Escape') resetTimeDraft() }} /></label>
                              <span className="time-separator" aria-hidden="true">．</span>
                              <label className="field"><span>0.1 秒</span><input type="number" min="0" max="9" step="1" inputMode="numeric" value={startTimeDraft.tenths} aria-invalid={timeDraftError === '0.1 秒必須介於 0 到 9。'} aria-describedby={timeDraftError ? 'start-time-error' : undefined} onChange={(event) => { setStartTimeDraft((value) => ({ ...value, tenths: event.target.value })); setDirty(true) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTimeDraft() } if (event.key === 'Escape') resetTimeDraft() }} /></label>
                            </div>
                            {timeDraftError && <small id="start-time-error" className="field-error" role="alert">{timeDraftError}</small>}
                          </fieldset>
                          <fieldset className="time-input-group" aria-describedby={timeDraftError ? 'end-time-error' : undefined} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) commitTimeDraft() }}>
                            <legend>結束時間</legend>
                            <div className="time-inputs">
                              <label className="field"><span>分</span><input ref={endInputRef} type="number" min="0" step="1" inputMode="numeric" value={endTimeDraft.minutes} aria-invalid={Boolean(timeDraftError) || saveAttempted && !Number.isFinite(selectedNode.end)} aria-describedby={timeDraftError ? 'end-time-error' : undefined} onChange={(event) => { setEndTimeDraft((value) => ({ ...value, minutes: event.target.value })); setDirty(true) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTimeDraft() } if (event.key === 'Escape') resetTimeDraft() }} /></label>
                              <span className="time-separator" aria-hidden="true">：</span>
                              <label className="field"><span>秒</span><input type="number" min="0" max="59" step="1" inputMode="numeric" value={endTimeDraft.seconds} aria-invalid={Boolean(timeDraftError)} aria-describedby={timeDraftError ? 'end-time-error' : undefined} onChange={(event) => { setEndTimeDraft((value) => ({ ...value, seconds: event.target.value })); setDirty(true) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTimeDraft() } if (event.key === 'Escape') resetTimeDraft() }} /></label>
                              <span className="time-separator" aria-hidden="true">．</span>
                              <label className="field"><span>0.1 秒</span><input type="number" min="0" max="9" step="1" inputMode="numeric" value={endTimeDraft.tenths} aria-invalid={Boolean(timeDraftError)} aria-describedby={timeDraftError ? 'end-time-error' : undefined} onChange={(event) => { setEndTimeDraft((value) => ({ ...value, tenths: event.target.value })); setDirty(true) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitTimeDraft() } if (event.key === 'Escape') resetTimeDraft() }} /></label>
                            </div>
                            {timeDraftError && <small id="end-time-error" className="field-error" role="alert">{timeDraftError}</small>}
                          </fieldset>
                          <button className="button button--danger button--full" type="button" onClick={removeNode}>刪除這個段落</button>
                        </>
                      ) : <p className="muted">從列表選擇一個段落後設定名稱、開始時間與開頭歌詞。</p>}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {renderSaveArea('segments')}
        </section>
        <section className={`panel editor-block ${!nodesReady ? 'editor-block--disabled' : ''}`} aria-labelledby="connections-heading" aria-disabled={!nodesReady}><div className="panel-heading"><div className="editor-block__title"><span className="editor-block__number">3</span><div><h2 id="connections-heading">設定可選連接</h2><span className="editor-block__status">{nodesReady ? '可選設定' : '等待有效段落'}</span></div></div></div>
          {!nodesReady ? <p className="editor-block__notice">請先在第 2 區完成至少一個有效段落。</p> : <div className="connections-layout"><section className="connections-source-pane" aria-labelledby="connections-source-heading"><h3 id="connections-source-heading">選擇段落</h3><div className="node-list node-list--sources">{orderedNodes.map((node, index) => renderNodeButton(node, index, true))}</div></section><section className="editor-settings connections-candidates-pane" aria-labelledby="connections-candidates-heading">{selectedNode ? <><h3 id="connections-candidates-heading">可播放段落</h3><label className="field connection-preview-duration"><span>試聽接法長度</span><span className="input-suffix"><input type="number" min="0.5" max="10" step="0.1" inputMode="decimal" value={connectionPreviewSeconds} disabled={editorPreviewBusy} aria-invalid={connectionPreviewTouched && !connectionPreviewValid} aria-describedby={connectionPreviewTouched && !connectionPreviewValid ? 'connection-preview-seconds-error' : undefined} onChange={(event) => { setConnectionPreviewSeconds(event.target.value); setConnectionPreviewTouched(true) }} onBlur={() => setConnectionPreviewTouched(true)} onKeyDown={(event) => { if (event.key === 'Enter') setConnectionPreviewTouched(true) }} /><span>秒</span></span>{connectionPreviewTouched && !connectionPreviewValid && <small id="connection-preview-seconds-error" className="field-error">請輸入 0.5 到 10.0 秒。</small>}</label>{!editorPreviewReady && <p id="connection-preview-ready-hint" className="muted" role="status">播放器準備中，請稍候。</p>}<fieldset className="edge-fieldset"><legend className="sr-only">可播放段落</legend>{orderedNodes.map((node) => { const checked = config.edges.some((edge) => edge.from === selectedNode.id && edge.to === node.id); return <div className="connection-candidate" key={node.id}><label className="checkbox-row"><input type="checkbox" aria-label={node.label} checked={checked} disabled={editorPreviewBusy} onChange={(event) => toggleEdge(node.id, event.target.checked)} /><span className="checkbox-row__content"><span>{node.label}</span>{node.openingLyric && <small aria-hidden="true">開頭歌詞：{node.openingLyric}</small>}</span></label>{checked && <button className="button connection-preview-button" type="button" disabled={editorPreviewBusy || !connectionPreviewValid || !editorPreviewReady} aria-describedby={!editorPreviewReady ? 'connection-preview-ready-hint' : undefined} onClick={(event) => startConnectionPreview(node, event.currentTarget)}>{editorPreviewBusy && connectionPreview?.candidateLabel === node.label ? '試聽中…' : '試聽接法'}</button>}</div> })}</fieldset>{editorPreviewBusy && connectionPreview && <p role="status">正在試聽接法「{connectionPreview.sourceLabel}」→「{connectionPreview.candidateLabel}」：第 {connectionPreview.phase} 段</p>}</> : <><h3 id="connections-candidates-heading">可播放段落</h3><p className="muted">從來源節點列表選擇一個段落。</p></>}</section></div>}{renderSaveArea('connections')}
        </section>
        <div className="sr-only" role="status" aria-live="polite">{playbackAnnouncement}</div>
        {saveAttempted && effectiveValidationErrors.length > 0 && <div className="validation-box" role="alert"><strong>儲存前請修正</strong><ul>{effectiveValidationErrors.slice(0, 6).map((item) => <li key={item}>{item}</li>)}</ul></div>}
      </div>
      {pendingVideo && (
        <div className="modal-backdrop" role="presentation" onKeyDown={(event) => { if (event.key === 'Escape') cancelPendingVideo() }}>
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="replace-video-title">
            <h2 id="replace-video-title">更換歌曲並清除段落？</h2>
            <p>更換歌曲會清除目前畫面中的所有段落與可選連接；按下儲存後將無法還原。</p>
            <div className="button-row modal-actions">
              <button ref={cancelDialogRef} className="button" type="button" onClick={cancelPendingVideo}>取消</button>
              <button className="button button--danger" type="button" onClick={() => applyVerifiedVideo(pendingVideo)}>更換歌曲並清除</button>
            </div>
          </section>
        </div>
      )}
      {pendingAlignment && (
        <div className="modal-backdrop" role="presentation" onKeyDown={(event) => { if (event.key === 'Escape') setPendingAlignment(null) }}>
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="align-title">
            <h2 id="align-title">讓前一段接到新段落？</h2>
            <p>{Number.isFinite(pendingAlignment.previousNode.end)
              ? `前一段「${pendingAlignment.previousNode.label}」目前結束於 ${formatTime(pendingAlignment.previousNode.end as number)}，晚於新段落的開始時間 ${formatTime(pendingAlignment.node.start)}。要將前一段結束時間調整為 ${formatTime(pendingAlignment.node.start)} 嗎？`
              : `前一段「${pendingAlignment.previousNode.label}」尚未設定結束時間。要將它的結束時間設為新段落的開始時間 ${formatTime(pendingAlignment.node.start)} 嗎？`}</p>
            <div className="button-row modal-actions">
              <button ref={cancelDialogRef} className="button" type="button" onClick={() => addPendingNode(pendingAlignment.node, false)}>保留原時間並新增</button>
              <button className="button button--primary" type="button" onClick={() => addPendingNode(pendingAlignment.node, true)}>對齊並新增</button>
            </div>
          </section>
        </div>
      )}
      {saveConfirmationOpen && (
        <div className="modal-backdrop" role="presentation" onKeyDown={(event) => { if (event.key === 'Escape') { pendingSaveConfigRef.current = null; setSaveConfirmationOpen(false) } }}>
          <section className="modal" role="dialog" aria-modal="true" aria-labelledby="publish-save-title">
            <h2 id="publish-save-title">要同時公開這個作品嗎？</h2>
            <p>這次儲存可以同時公開作品。公開後，其他人可以透過分享連結播放。</p>
            <div className="button-row modal-actions">
              <button ref={cancelDialogRef} className="button" type="button" onClick={() => { pendingSaveConfigRef.current = null; setSaveConfirmationOpen(false) }}>取消</button>
              <button className="button" type="button" onClick={() => void save(false, pendingSaveConfigRef.current ?? undefined)}>保持不公開並儲存</button>
              <button className="button button--primary" type="button" onClick={() => void save(true, pendingSaveConfigRef.current ?? undefined)}>儲存並公開</button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
